import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { FamilyEvent } from '@/types/family';
import { supabase } from '@/lib/supabase';
import { subscribeWithRetry } from '@/lib/realtime';
import { useAuth } from '@/contexts/AuthContext';
import { useFamilyGraph } from '@/contexts/FamilyGraphContext';

interface EventContextType {
  events: FamilyEvent[];
  addEvent: (
    event: Omit<FamilyEvent, 'id'>,
  ) => Promise<void>;
  rsvpToEvent: (
    eventId: string,
    status: 'yes' | 'no' | 'maybe',
  ) => Promise<void>;
}

interface FamilyEventRow {
  id: string;
  owner_user_id: string;
  created_by: string;
  title: string;
  event_date: string;
  event_type: FamilyEvent['type'];
  location: string;
  description: string;
  image_url: string | null;
}

interface FamilyEventRsvpRow {
  id: string;
  event_id: string;
  owner_user_id: string;
  member_id: string;
  responder_user_id: string;
  status: 'yes' | 'no' | 'maybe';
}

const DEFAULT_EVENT_IMAGE =
  'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=800';

const EventContext =
  createContext<EventContextType | undefined>(undefined);

export const EventProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const {
    effectiveOwnerUserId,
    viewerMemberId,
    members,
    loading: graphLoading,
  } = useFamilyGraph();
  const [events, setEvents] = useState<FamilyEvent[]>([]);

  const tenantOwner =
    !graphLoading && user?.id
      ? effectiveOwnerUserId ?? user.id
      : null;

  const refreshEvents = useCallback(async () => {
    if (!user?.id || !tenantOwner) {
      setEvents([]);
      return;
    }

    const [eventResponse, rsvpResponse] = await Promise.all([
      supabase
        .from('family_events')
        .select('*')
        .eq('owner_user_id', tenantOwner)
        .order('event_date', { ascending: true }),
      supabase
        .from('family_event_rsvps')
        .select('*')
        .eq('owner_user_id', tenantOwner),
    ]);

    if (eventResponse.error) {
      throw eventResponse.error;
    }
    if (rsvpResponse.error) {
      throw rsvpResponse.error;
    }

    const memberNames = new Map(
      members.map((member) => [member.id, member.name]),
    );
    const rsvps = (rsvpResponse.data ?? []) as FamilyEventRsvpRow[];
    const rsvpsByEvent = new Map<string, FamilyEventRsvpRow[]>();

    for (const rsvp of rsvps) {
      const bucket = rsvpsByEvent.get(rsvp.event_id) ?? [];
      bucket.push(rsvp);
      rsvpsByEvent.set(rsvp.event_id, bucket);
    }

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const projected = ((eventResponse.data ?? []) as FamilyEventRow[])
      .map((row): FamilyEvent => {
        const eventRsvps = rsvpsByEvent.get(row.id) ?? [];
        return {
          id: row.id,
          title: row.title,
          date: row.event_date,
          location: row.location,
          description: row.description,
          type: row.event_type,
          image: row.image_url ?? DEFAULT_EVENT_IMAGE,
          attendees: eventRsvps
            .filter((rsvp) => rsvp.status === 'yes')
            .map((rsvp) => rsvp.member_id),
          rsvps: eventRsvps.map((rsvp) => ({
            name: memberNames.get(rsvp.member_id) ?? 'Family member',
            status: rsvp.status,
          })),
          isPast: new Date(`${row.event_date}T00:00:00`) < startOfToday,
        };
      });

    setEvents(projected);
  }, [members, tenantOwner, user?.id]);

  useEffect(() => {
    if (!tenantOwner || !user?.id) {
      setEvents([]);
      return;
    }

    void refreshEvents().catch((error) => {
      console.error('Failed to load canonical family events', error);
      setEvents([]);
    });
  }, [refreshEvents, tenantOwner, user?.id]);

  useEffect(() => {
    if (!tenantOwner || !user?.id) return;

    const refresh = () => {
      void refreshEvents().catch((error) => {
        console.error('Failed to refresh canonical family events', error);
      });
    };

    const channel = supabase
      .channel(`canonical-event-sync:${tenantOwner}`)
      .on(
        'postgres_changes' as any,
        {
          event: '*',
          schema: 'public',
          table: 'family_events',
          filter: `owner_user_id=eq.${tenantOwner}`,
        },
        refresh,
      )
      .on(
        'postgres_changes' as any,
        {
          event: '*',
          schema: 'public',
          table: 'family_event_rsvps',
          filter: `owner_user_id=eq.${tenantOwner}`,
        },
        refresh,
      );

    return subscribeWithRetry(channel, {
      label: 'canonical-event-sync',
      maxAttempts: 3,
      toastOnFailure: true,
    });
  }, [refreshEvents, tenantOwner, user?.id]);

  const addEvent = async (
    event: Omit<FamilyEvent, 'id'>,
  ) => {
    if (!user?.id || !tenantOwner) {
      throw new Error('Sign in to a family tree before creating an event.');
    }

    const { error } = await supabase
      .from('family_events')
      .insert({
        owner_user_id: tenantOwner,
        created_by: user.id,
        title: event.title.trim(),
        event_date: event.date,
        event_type: event.type,
        location: event.location.trim(),
        description: event.description ?? '',
        image_url: event.image ?? null,
      });

    if (error) {
      throw error;
    }

    await refreshEvents();
  };

  const rsvpToEvent = async (
    eventId: string,
    status: 'yes' | 'no' | 'maybe',
  ) => {
    if (!user?.id || !tenantOwner || !viewerMemberId) {
      throw new Error(
        'Your signed-in account must be bound to a family member before RSVPing.',
      );
    }

    const viewerMember = members.find(
      (member) => member.id === viewerMemberId,
    );

    if (
      !viewerMember ||
      viewerMember.ownerUserId !== tenantOwner ||
      viewerMember.claimedByUserId !== user.id
    ) {
      throw new Error('Canonical family member identity is not available for this tree.');
    }

    const { data: existing, error: lookupError } = await supabase
      .from('family_event_rsvps')
      .select('id')
      .eq('event_id', eventId)
      .eq('member_id', viewerMemberId)
      .maybeSingle();

    if (lookupError) {
      throw lookupError;
    }

    if (existing?.id) {
      const { error } = await supabase
        .from('family_event_rsvps')
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id);

      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('family_event_rsvps')
        .insert({
          event_id: eventId,
          owner_user_id: tenantOwner,
          member_id: viewerMemberId,
          responder_user_id: user.id,
          status,
        });

      if (error) {
        if (error.code === '23505') {
          const { error: raceUpdateError } = await supabase
            .from('family_event_rsvps')
            .update({
              status,
              updated_at: new Date().toISOString(),
            })
            .eq('event_id', eventId)
            .eq('member_id', viewerMemberId);

          if (raceUpdateError) throw raceUpdateError;
        } else {
          throw error;
        }
      }
    }

    await refreshEvents();
  };

  return (
    <EventContext.Provider
      value={{
        events,
        addEvent,
        rsvpToEvent,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

export const useEvents = (): EventContextType => {
  const context = useContext(EventContext);

  if (!context) {
    throw new Error(
      'useEvents must be used within EventProvider',
    );
  }

  return context;
};
